import { CalendarX } from 'lucide-react';

import type { ScheduleEvent } from '../../../services/schedule';

import { ClassCard } from './ClassCard';
import { toDateKey, isToday } from './scheduleDateUtils';

interface DayCalendarProps {
  anchor: Date;
  eventsByDate: Record<string, ScheduleEvent[]>;
  enrolledClassIds: Set<string>;
  checkedInClassIds: Set<string>;
  isLoading: boolean;
  onEventClick: (event: ScheduleEvent) => void;
}

export function DayCalendar({
  anchor,
  eventsByDate,
  enrolledClassIds,
  checkedInClassIds,
  isLoading,
  onEventClick,
}: DayCalendarProps) {
  const key = toDateKey(anchor);
  const dayEvents = eventsByDate[key] ?? [];
  const today = isToday(anchor);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl border border-b-0 border-[#363232] ${
          today ? 'bg-[#9B1421]/10' : 'bg-[#1A1717]'
        }`}
      >
        <span
          className={`text-sm font-bold ${today ? 'text-[#9B1421]' : 'text-[#EBFFEE]'}`}
        >
          {anchor.toLocaleDateString([], {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
          })}
        </span>
        {today && (
          <span className="text-[10px] bg-[#9B1421] text-white px-1.5 py-0.5 rounded-full font-medium">
            Today
          </span>
        )}
        <span className="text-xs text-[#A9A3A3] ml-auto">
          {dayEvents.length} {dayEvents.length === 1 ? 'class' : 'classes'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto bg-[#1A1717] border border-[#363232] rounded-b-xl p-3">
        {isLoading && dayEvents.length === 0 ? (
          <div className="space-y-2">
            <div className="h-14 bg-[#2A2626] rounded animate-pulse" />
            <div className="h-14 bg-[#2A2626] rounded animate-pulse" />
            <div className="h-14 bg-[#2A2626] rounded animate-pulse" />
          </div>
        ) : dayEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarX size={36} className="text-[#504A4A] mb-3" />
            <p className="text-[#A9A3A3] text-sm">No classes scheduled.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dayEvents.map((event) => (
              <ClassCard
                key={event.event_id}
                event={event}
                isEnrolled={enrolledClassIds.has(event.event_id)}
                isCheckedIn={checkedInClassIds.has(event.event_id)}
                onClick={() => onEventClick(event)}
                view="day"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
